import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Award, 
  GraduationCap, 
  Globe, 
  Mail, 
  Phone, 
  Calendar, 
  Send,
  Building2,
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  MapPin
} from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { formatUzPhone, localizeLawyerPosition, localizeSpecialization, localizeLanguages } from '../../utils/helpers';
import InstantBookingModal from '../../components/booking/InstantBookingModal';

function InstagramIcon({ className = "w-4 h-4" }) {
  return (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export default function LawyerDetailPage() {
  const { slug } = useParams();
  const { currentLang, t, localized } = useLanguage();
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const [primaryOffice, setPrimaryOffice] = useState(null);

  useEffect(() => {
    api.get(`/lawyers/${slug}`)
      .then((res) => {
        if (res?.success) setLawyer(res.lawyer);
      })
      .finally(() => setLoading(false));

    api.get('/offices').then((res) => {
      const list = res?.offices || res?.data?.offices || [];
      if (list.length > 0) setPrimaryOffice(list[0]);
    }).catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#05080e] min-h-screen py-24 text-center text-slate-400">
        {currentLang === 'uz' ? 'Advokat ma’lumotlari yuklanmoqda...' : currentLang === 'ru' ? 'Загрузка профиля адвоката...' : 'Loading attorney profile...'}
      </div>
    );
  }

  if (!lawyer) {
    return (
      <div className="bg-[#05080e] min-h-screen py-24 text-center text-slate-200">
        <h2 className="text-xl font-bold font-serif mb-4">
          {currentLang === 'uz' ? 'Advokat topilmadi' : currentLang === 'ru' ? 'Адвокат не найден' : 'Attorney Not Found'}
        </h2>
        <Link to="/lawyers" className="text-[#c59b27] text-sm font-semibold hover:underline">
          &larr; {currentLang === 'uz' ? 'Barcha advokatlarga qaytish' : currentLang === 'ru' ? 'Вернуться к списку адвокатов' : 'Return to all attorneys'}
        </Link>
      </div>
    );
  }

  const bioText = currentLang === 'uz' 
    ? (lawyer.bioUz || lawyer.bio || '') 
    : currentLang === 'ru' 
    ? (lawyer.bioRu || lawyer.bioUz || lawyer.bio || '') 
    : (lawyer.bioEn || lawyer.bioUz || lawyer.bio || '');

  const officeAddress = currentLang === 'uz'
    ? "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi (Jizzax Advokatlari Markaziy Bosh Ofisi)"
    : currentLang === 'ru'
    ? "г. Джизак, улица Ислама Каримова, напротив Турон Банка (Центральный офис коллегии адвокатов «Jizzax Advokatlari»)"
    : "Jizzakh city, Islam Karimov street, opposite Turon Bank (Jizzax Advokatlari Central Headquarters)";

  const officeLat = primaryOffice?.latitude ?? 40.160331;
  const officeLng = primaryOffice?.longitude ?? 67.827246;
  const officeMapUrl = primaryOffice?.mapUrl || primaryOffice?.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${officeLat},${officeLng}`;
  const dynamicOfficeAddress = (currentLang === 'ru' ? primaryOffice?.addressRu : currentLang === 'en' ? primaryOffice?.addressEn : primaryOffice?.addressUz) || primaryOffice?.address || officeAddress;

  const phoneDisplay = lawyer.phone ? formatUzPhone(lawyer.phone) : '+998(97) 325-07-70';
  const phoneClean = (lawyer.phone || '+998973250770').replace(/\D/g, '');

  const telegramUrl = lawyer.telegram 
    ? (lawyer.telegram.startsWith('http') ? lawyer.telegram : `https://t.me/${lawyer.telegram.replace('@', '')}`)
    : 'https://t.me/Abdulazizxon_077';

  const instagramUrl = lawyer.instagram
    ? (lawyer.instagram.startsWith('http') ? lawyer.instagram : `https://instagram.com/${lawyer.instagram.replace('@', '')}`)
    : 'https://instagram.com/_abdullaev1c_';

  const emailDisplay = lawyer.email || '';

  const safeParseArray = (val, fallback) => {
    if (!val) return fallback;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return [val];
      }
    }
    return [val];
  };

  const educationList = safeParseArray(lawyer.education, [
    currentLang === 'uz'
      ? "Toshkent davlat yuridik universiteti (TDYuU) — Magistratura"
      : currentLang === 'ru'
      ? "Ташкентский государственный юридический университет (ТГЮУ) — Магистратура"
      : "Tashkent State University of Law (TSUL) — LL.M. Master of Laws"
  ]);

  const certsList = safeParseArray(lawyer.certifications, [
    currentLang === 'uz'
      ? "O‘zbekiston Respublikasi Advokatlar Palatasi a’zosi va davlat litsenziyasi"
      : currentLang === 'ru'
      ? "Член Палаты Адвокатов Республики Узбекистан (Государственная лицензия)"
      : "Chamber of Advocates of the Republic of Uzbekistan — Full Senior Bar License"
  ]);

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Header */}
      <section className="bg-[#070c16] text-white py-16 sm:py-24 border-b border-[#141d2d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Back Navigation Bar */}
          <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-[#141d2d]">
            <button
              type="button"
              onClick={() => window.history.length > 1 ? window.history.back() : window.location.href = '/lawyers'}
              className="inline-flex items-center space-x-1.5 text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:text-amber-200 py-1.5 px-3 rounded-lg bg-[#0b1220] border border-[#1b2b46] hover:border-[#c59b27]/60 cursor-pointer shadow-sm transition-colors"
            >
              <span>← {currentLang === 'uz' ? 'Orqaga qaytish' : currentLang === 'ru' ? 'Назад' : 'Go Back'}</span>
            </button>
            <Link to="/lawyers" className="text-xs text-slate-400 hover:text-white transition-colors">
              {currentLang === 'uz' ? 'Barcha advokatlar katalogi' : currentLang === 'ru' ? 'Каталог всех адвокатов' : 'All Attorneys'}
            </Link>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <span className="text-xs uppercase tracking-wider text-[#c59b27] font-semibold block mb-1">
                {localizeLawyerPosition(lawyer.position || 'Advokat', currentLang).toUpperCase()}
              </span>
              <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
                {lawyer.fullName}
              </h1>
              <p className="text-slate-400 text-xs sm:text-sm max-w-xl mt-2 font-light">
                {localizeSpecialization(lawyer.specialization, currentLang)} • {lawyer.experienceYears} {t('lawyer_experience_suffix', 'yillik tajriba')}
              </p>
            </div>

            <button
              onClick={() => setBookingModalOpen(true)}
              className="inline-flex items-center px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] rounded shadow-lg shadow-[#b38038]/25 transition-all cursor-pointer"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t('nav_book_cta', 'Qabulga yozilish')}
            </button>
          </div>
        </div>
      </section>

      {/* Profile Details */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            
            {/* Left Col: Photo & Contacts */}
            <div className="space-y-6">
              {/* Clear Photo */}
              <div className="rounded-2xl overflow-hidden border border-[#c59b27]/40 shadow-2xl bg-[#080d17]">
                <img
                  src={lawyer.photoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600"}
                  alt={lawyer.fullName}
                  className="w-full h-96 object-cover object-top filter brightness-105"
                />
              </div>

              {/* Workplace Info */}
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-5 space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#c59b27] flex items-center">
                  <Building2 className="w-3.5 h-3.5 mr-1.5" />
                  {currentLang === 'uz' ? 'Ishxona Manzili' : currentLang === 'ru' ? 'Рабочий Офис' : 'Office Location'}
                </span>
                <p className="text-xs text-slate-300 leading-relaxed font-medium">
                  {dynamicOfficeAddress}
                </p>
                <a
                  href={officeMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-[#c59b27] hover:underline inline-flex items-center font-semibold pt-1"
                >
                  {currentLang === 'en' ? 'View on Google Maps' : currentLang === 'ru' ? 'Посмотреть на Google Картах' : 'Google Xaritada ko‘rish'}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>

              {/* Direct Contacts Card (Phone, Email, Telegram, Instagram) */}
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-6 space-y-3.5 text-xs">
                <h4 className="font-serif font-bold text-slate-100 text-sm uppercase tracking-wider mb-2">
                  {currentLang === 'uz' ? 'To‘g‘ridan-to‘g‘ri Aloqa' : currentLang === 'ru' ? 'Прямые Контакты' : 'Direct Contacts'}
                </h4>

                <a href={`tel:+${phoneClean}`} className="flex items-center text-slate-300 hover:text-[#c59b27] transition-colors">
                  <Phone className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0" />
                  <span className="font-mono">{phoneDisplay}</span>
                </a>

                <a href={telegramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-slate-300 hover:text-[#229ed9] transition-colors">
                  <Send className="w-4 h-4 text-[#229ed9] mr-2.5 shrink-0" />
                  <span>Telegram: {lawyer.telegram ? (lawyer.telegram.startsWith('@') ? lawyer.telegram : `@${lawyer.telegram.replace('https://t.me/', '')}`) : '@Abdulazizxon_077'}</span>
                </a>

                <div className="flex items-center text-[#c59b27] font-semibold">
                  <ShieldCheck className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0" />
                  <span>{currentLang === 'uz' ? 'Bizda konsultatsiya bepul' : currentLang === 'ru' ? 'Консультация бесплатная' : 'Free consultation'}</span>
                </div>

                <div className="flex items-center text-slate-400 pt-2 border-t border-[#141d2d]">
                  <Globe className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0" />
                  <span>{currentLang === 'uz' ? 'Tillar:' : currentLang === 'ru' ? 'Языки:' : 'Languages:'} {localizeLanguages(lawyer.languages || "O‘zbek, Rus, Ingliz", currentLang)}</span>
                </div>
              </div>
            </div>

            {/* Right 2 Cols: Biography & Credentials */}
            <div className="lg:col-span-2 space-y-8">
              {/* Bio in Active Language */}
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-8">
                <h2 className="font-serif text-2xl font-bold text-slate-100 mb-4">
                  {currentLang === 'uz' ? 'Professional Tarjimai Hol' : currentLang === 'ru' ? 'Профессиональная Биография' : 'Professional Biography'}
                </h2>
                <p className="text-slate-300 text-xs sm:text-sm leading-relaxed font-light">
                  {bioText || "Jizzax Advokatlari jamoasining yetakchi advokati. Murakkab jinoyat, iqtisodiy va korporativ sud ishlarida fuqarolar hamda yirik korxonalarning qonuniy manfaatlarini ishonchli himoya qilib kelmoqda."}
                </p>
              </div>

              {/* Education */}
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-8">
                <h3 className="font-serif text-xl font-bold text-slate-100 mb-4 flex items-center">
                  <GraduationCap className="w-5 h-5 text-[#c59b27] mr-2" />
                  {currentLang === 'uz' ? 'Ta’lim va Ilmiy Darajalar' : currentLang === 'ru' ? 'Образование и Квалификация' : 'Education & Credentials'}
                </h3>
                <ul className="space-y-2.5">
                  {educationList.map((edu, i) => (
                    <li key={i} className="flex items-start text-xs sm:text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-[#c59b27] mr-2.5 mt-0.5 shrink-0" />
                      <span>{edu}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Certifications & Bar Licenses */}
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-8">
                <h3 className="font-serif text-xl font-bold text-slate-100 mb-4 flex items-center">
                  <ShieldCheck className="w-5 h-5 text-[#c59b27] mr-2" />
                  {currentLang === 'uz' ? 'Litsenziyalar va Attestatsiya' : currentLang === 'ru' ? 'Адвокатская Лицензия и Сертификаты' : 'Bar Admissions & Licenses'}
                </h3>
                <ul className="space-y-2.5">
                  {certsList.map((cert, i) => (
                    <li key={i} className="flex items-start text-xs sm:text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-[#c59b27] mr-2.5 mt-0.5 shrink-0" />
                      <span>{cert}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <InstantBookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)} 
        initialLawyerId={lawyer?.id}
      />
    </div>
  );
}
